'use client';

import { usePreservedCallback } from '@toss/react';
import { useTranslation } from 'next-i18next';
import { match } from 'ts-pattern';
import { useDialog } from '@zep/dialog';
import { useExpectedPlanPrice, usePlanStatusType } from '../hooks';
import { usePlanSubscriptionAction } from '../hooks/usePlanSubscriptionAction';
import { usePlanPaymentFormContext, usePlanPaymentInfoContext } from '../PlanPaymentSection/Providers';
import { PlanTemplateType } from '../types';
import { getVatPreview } from '../utils';
import { SharedPlanPaymentButton } from './shared';

/**
 * 정기 결제하기 버튼은 국내와 해외에서 모두 사용됩니다.
 * 국내, 해외 둘 다 서버에 먼저 카드를 등록하여 BillingInfo를 등록해두고 서버에 해당 BillingKey를 통해 정기적인 결제를 요청하는 방식
 */
export const PlanPaymentSubmit = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    selectedPlanInfo,
    isKrPayment,
    isUpdate,
    spaceHashId,
    currencyString,
    userCountryCode
  } = usePlanPaymentInfoContext();
  const {
    watch,
    handleSubmit
  } = usePlanPaymentFormContext();
  const planType = watch('planType');
  const maxCcu = watch('maxCcu');
  const planTemplateType = watch('planTemplateType');
  const dialog = useDialog();
  const {
    handleSubscribePlan: _handleSubscribePlan
  } = usePlanSubscriptionAction();
  const planStatusType = usePlanStatusType({
    planType
  });
  const {
    추가결제예상금액
  } = useExpectedPlanPrice({
    selectedPlanInfo,
    enabled: isUpdate && planTemplateType === PlanTemplateType.PRODUCT_SUBSCRIPTION,
    spaceHashId
  });
  const isButtonDisabled = planType === 'enterprise' || isUpdate && planStatusType === 'SAME_GRADE' || maxCcu >= 300;
  const buttonPrice = match([isUpdate, planStatusType]).with([true, 'UP_GRADE'], () => 추가결제예상금액 < 0 ? 0 : 추가결제예상금액).with([true, 'DOWN_GRADE'], () => 0)
  // 신규 구독 첫 결제는 판매 템플릿의 서버 priceInfo를 우선 사용하고,
  // 구버전 응답이면 기존 preview로 fallback한다.
  .otherwise(() => selectedPlanInfo.priceInfo?.finalPrice ?? getVatPreview({
    amount: selectedPlanInfo.salePrice,
    currency: isKrPayment ? 'KRW' : 'USD',
    userCountryCode,
    scenario: 'NEW_SUBSCRIPTION_FIRST_PAYMENT'
  }).finalPrice);
  const handleSubscribePlan = usePreservedCallback(async () => {
    try {
      await _handleSubscribePlan({
        retry: async () => {
          await handleSubscribePlan();
        },
        isRemoveBillingKey: true,
        // GTM purchase 신호용 실청구액 (버튼에 표시된 결제 금액과 동일).
        // 통화는 플랜 템플릿 값을 그대로 쓴다 (JP 플랜은 JPY).
        chargedAmount: {
          value: buttonPrice,
          currency: selectedPlanInfo.currency || (isKrPayment ? 'KRW' : 'USD')
        }
      });
    } catch (_e) {
      //
    }
  });
  const onSubmit = () => {
    if (
    // 100원 혹은 1달러 미만 결제는 불가능 - 단, 0원 이하는 PG사를 통하지 않으므로 제외
    isKrPayment && buttonPrice > 0 && buttonPrice < 100 || !isKrPayment && buttonPrice > 0 && buttonPrice < 1) {
      dialog.alert({
        title: t('subscription:payment .alertPayFail.title', '결제 실패'),
        content: t('subscription:payment .alertPayFail.descriptionMinimumPrice', {
          defaultValues: '추가 결제 금액이 {{minimumPrice}} 미만인 경우 결제가 불가능합니다. 플랜을 변경하거나 고객센터를 통해 문의해주세요.',
          minimumPrice: `${currencyString}${isKrPayment ? '100' : '1'}`
        })
      });
      return;
    }
    handleSubscribePlan();
  };
  const onError = () => {
    // FIXME: Input의 화면 상단에 있는 경우에 깨지는 코드이므로 그때는 고쳐야 함
    // input 오류 발생 시 에러 메시지를 확인할 수 있도록 스크롤을 최하단으로 이동
    // 문제가 발생하는 input으로 focus하는 방식으로 개선해야 함(비용이 커서 나중으로 미룸)
    window.scrollTo(0, document.body.scrollHeight);
  };
  return <SharedPlanPaymentButton isButtonDisabled={isButtonDisabled} buttonPrice={buttonPrice} currencyString={currencyString} onClick={handleSubmit(onSubmit, onError)} data-sentry-element="SharedPlanPaymentButton" data-sentry-component="PlanPaymentSubmit" data-sentry-source-file="PlanPaymentSubmit.tsx" />;
};